import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';
import moment from 'moment';

import { Heading, Paragraph, PlaceholderButton } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import QuillText from 'packages/kakapo-ui-kit/components/editors/QuillText';

import GoalCard from './GoalCard';

const Status = styled(Paragraph)`
	color: ${(props) => props.theme.colors.primary};
`;

function GoalMoment(props: { userID: string; instance: graphql.MomentInstance }) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const required = props.instance.momentTemplate.goals?.minimumGoals ?? 1;
	const count = props.instance.goals?.length ?? 0;

	return (
		<Box mb={4}>
			<Flex mb={3}>
				<Box flexGrow={1} flexShrink={1}>
					<Heading mb={1}>{localize(props.instance.momentTemplate.title).text}</Heading>
					<Paragraph>
						<QuillText quill={localize(props.instance.momentTemplate.description ?? [])?.text} />
					</Paragraph>
				</Box>
				{(props.instance.momentTemplate.goals?.minimumGoals ?? 0) > 0 && (
					<Box ml={2} flexGrow={0} flexShrink={0}>
						<Status>
							{count}/{required} {t('goals.moments.count')}
						</Status>
					</Box>
				)}
			</Flex>
			{props.instance.goals?.map((goal) => (
				<Box key={goal.id} mb={2}>
					<GoalCard goal={goal as graphql.Goal} />
				</Box>
			))}
			<PlaceholderButton
				title={t('goals.moments.connect')}
				to={`/users/${props.userID}/goals/new?moment_id=${props.instance.id}`}
			/>
		</Box>
	);
}

export default function GoalMoments(props: { userID: string }) {
	const query = graphql.useGoalMomentsQuery({
		variables: { id: props.userID },
		fetchPolicy: 'network-only',
	});

	return (
		<Box>
			{query.data?.user.moments
				.filter((instance) => instance.status === graphql.MomentStatus.Open)
				.filter((instance) => moment(instance.endDate).isAfter(moment().subtract(2, 'months')))
				.filter((instance) => instance.momentTemplate.type === graphql.MomentType.Goals)
				.map((instance) => (
					<GoalMoment
						key={instance.id}
						userID={props.userID}
						instance={instance as graphql.MomentInstance}
					/>
				))}
		</Box>
	);
}
